import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { parseVariantGroups } from '../../util/variants';

import css from './VariantSelector.module.css';

const getUuidString = maybeUuid => {
  if (!maybeUuid) return '';
  if (typeof maybeUuid === 'string') return maybeUuid;
  if (typeof maybeUuid.uuid === 'string') return maybeUuid.uuid;
  if (typeof maybeUuid.id?.uuid === 'string') return maybeUuid.id.uuid;
  return '';
};

const VariantSelector = props => {
  const { variantsRaw, currentListingId, mode, className } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const groups = useMemo(() => parseVariantGroups(variantsRaw), [variantsRaw]);
  const currentId = useMemo(() => getUuidString(currentListingId), [currentListingId]);

  const initialSelectedId = useMemo(() => {
    if (!groups.length) return '';

    if (currentId) {
      const anyGroupHasCurrent = groups.some(g => g.options.some(o => o.productId === currentId));
      if (anyGroupHasCurrent) return currentId;
    }

    // Fallback to first available option from the first group
    return groups?.[0]?.options?.[0]?.productId || '';
  }, [currentId, groups]);

  const [selectedProductId, setSelectedProductId] = useState(initialSelectedId);

  useEffect(() => {
    setSelectedProductId(initialSelectedId);
  }, [initialSelectedId]);

  const navigateToProductId = useCallback(
    (nextId, slugParts = []) => {
      if (!nextId || nextId === selectedProductId) return;

      setSelectedProductId(nextId);

      // If we are already on this product, do nothing.
      if (currentId && nextId === currentId) return;

      const slugBase = slugParts.filter(Boolean).join(' ');
      const slug = createSlug(slugBase);

      const nextUrl =
        routeConfiguration && Array.isArray(routeConfiguration)
          ? createResourceLocatorString('ListingPage', routeConfiguration, { id: nextId, slug }, {})
          : `/product/${slug}/${nextId}`;

      history.push(nextUrl);
    },
    [currentId, history, routeConfiguration, selectedProductId]
  );

  if (!groups.length) return null;

  const visibilityClass =
    mode === 'desktop' ? css.desktopOnly : mode === 'mobile' ? css.mobileOnly : null;

  return (
    <div className={classNames(css.root, visibilityClass, className)}>
      {groups.map((group, idx) => {
        const options = group.options || [];
        if (options.length < 2) return null;

        const groupSelectedId = options.some(o => o.productId === selectedProductId)
          ? selectedProductId
          : options.some(o => o.productId === currentId)
          ? currentId
          : options?.[0]?.productId;

        return (
          <div
            key={`${group.variantId ||
              group.variantType ||
              group.variantLabel ||
              'variant'}-${idx}`}
            className={css.group}
          >
            <div className={css.labelRow}>
              <div className={css.label}>{group.variantLabel}</div>
            </div>
            <div className={css.options} role="group" aria-label={group.variantLabel}>
              {options.map(o => {
                const isSelected = o.productId === groupSelectedId;
                const slugParts = [group.variantName, o.optionLabel];
                return (
                  <button
                    key={o.productId}
                    type="button"
                    className={classNames(css.option, { [css.optionSelected]: isSelected })}
                    aria-pressed={isSelected}
                    onClick={() => navigateToProductId(o.productId, slugParts)}
                  >
                    {o.thumbnailUrl ? (
                      <img
                        className={css.thumbnail}
                        src={o.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className={css.thumbnailPlaceholder} aria-hidden="true" />
                    )}
                    <span className={css.optionLabel}>{o.optionLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VariantSelector;
